import React from 'react';
import searchboxAutosuggestSource from 'data/sources/searchboxAutosuggestSource';
import tractsByTractcodesSource from 'data/sources/tractsByTractcodesSource';
import countiesByCountyIdsSource from 'data/sources/countiesByCountyIdsSource';
import statesByStatenamesSource from 'data/sources/statesByStatenamesSource';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { executeSQL } from '@carto/react-api';
import { useSelector, useDispatch } from 'react-redux';
import SearchBox from 'components/common/SearchBox';
import Accordion from 'components/common/Accordion';
import InputRadioLabelGroup from './InputRadioLabelGroup';
import NestedRadioInputs from './NestedRadioInputs';
import LayerToggleActions from './LayerToggleActions';
// import InputRadioGroup from './InputRadioGroup';
import Button from '../common/Button';
// import ButtonSelect from '../common/ButtonSelect';
import InputToggle from '../common/InputToggle';
import { setViewState } from '@carto/react-redux';
import toast from 'react-hot-toast';
// import { ACTIVE_TRACT_BROADBAND_LAYER_ID } from 'components/layers/ActiveTractBroadbandLayer';
// import { addLayer, removeLayer, addSource, removeSource } from '@carto/react-redux';
import { generateCountyId } from '../../utils/countyId';
import {
  TRACT_PANEL_TYPES,
  COUNTY_PANEL_TYPES,
  STATE_PANEL_TYPES,
} from 'utils/panel-types';
import * as Slider from '@radix-ui/react-slider';
import { ReactComponent as CloseLarge } from 'assets/img/close-large.svg';
import { ReactComponent as SliderLabel } from 'assets/img/slider-label.svg';

const YEARS_OPTIONS = ['2015', '2016', '2017', '2018', '2019', '2020', '2021'];

export default function FilterBox({
  children: _children,
  year,
  setYear,
  socioeconomicFactor,
  setSocioeconomicFactor,
  features,
  setFeatures: _sf,
  isSocioeconomicFactorsLayerActive,
  setIsSocioeconomicFactorsLayerActive,
  handleShowSimilarTractsClick,
  broadbandLayers: _bl,
  activeBroadbandLayer: _abl,
  setActiveBroadbandLayer: _setabl,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState('');
  const [isLayerToggleActionsOpen, setIsLayerToggleActionsOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [didMount, setDidMount] = useState(false);
  const queryClient = useQueryClient();
  const credentials = useSelector((state) => state.carto.credentials);
  const queryFn = useCallback(
    async ({ signal }) => {
      let searchAutoSuggestSource = searchboxAutosuggestSource(
        searchQuery,
        'all',
        0,
        year
      );

      return await executeSQL({
        credentials: credentials,
        query: searchAutoSuggestSource.data,
        connection: searchAutoSuggestSource.connection,
        opts: {
          abortController: {
            signal,
          },
        },
      });
    },
    [credentials, searchQuery, year]
  );
  const dispatch = useDispatch();

  const zoomIntoView = useCallback(
    ({ lng, lat, zoom }) => {
      dispatch(
        setViewState({
          zoom,
          latitude: lat,
          longitude: lng,
        })
      );
    },
    [dispatch]
  );

  const { data: searchResults, isPending: isSearchResultsLoading } = useQuery({
    queryKey: ['searchSuggestions', searchQuery],
    queryFn,
  });

  // On mount.
  useEffect(() => {
    if (didMount === true) {
      return;
    }

    async function fetchData() {
      let activeState = searchParams.get('active-state') || null;
      let activeCounty = searchParams.get('active-county') || null;
      let activeTract = searchParams.get('active-tract') || null;

      let active = activeState || activeCounty || activeTract;

      if (active === null) {
        return;
      }

      let result = await queryClient.fetchQuery({
        queryKey: ['searchSuggestions', active],
        queryFn: async ({ signal }) => {
          let searchQuery = '';

          if (activeCounty !== null) {
            searchQuery = activeCounty.split(',')?.[0] || '';
          } else {
            searchQuery = active;
          }

          let searchAutoSuggestSource = searchboxAutosuggestSource(
            searchQuery,
            'all',
            0,
            year
          );

          return await executeSQL({
            credentials: credentials,
            query: searchAutoSuggestSource.data,
            connection: searchAutoSuggestSource.connection,
            opts: {
              abortController: {
                signal,
              },
            },
          });
        },
      });

      let selected = null;

      if (activeTract !== null && result.length === 3) {
        selected = result.find((item) => item.type === 'tract');
        setSearchQuery(selected?.tractcode);
      } else if (activeCounty !== null) {
        selected = result.find((item) => item.type === 'county');

        setSearchQuery(selected?.countyname);
      } else if (activeState !== null) {
        selected = result.find((item) => item.type === 'state');
        setSearchQuery(selected?.statename);
      }

      setSelected(selected);
    }

    fetchData();

    setDidMount(true);
  }, [
    credentials,
    didMount,
    queryClient,
    queryFn,
    searchParams,
    searchQuery,
    searchResults,
    setSearchParams,
    setSearchQuery,
    year,
  ]);

  let updateSelected = useCallback(
    function (selected) {
      if (selected === null) {
        setSearchParams((params) => {
          params.delete('active-state');
          params.delete('active-county');
          params.delete('active-tract');
          params.delete('active-panel');
          params.delete('similar-tracts');
          params.delete('active-compare-tract');
          params.delete('active-compare-county');
          params.delete('active-compare-state');
          params.delete('active-compare-similar-tract');

          return params;
        });
      } else {
        setSearchParams((params) => {
          if (selected.type === 'state') {
            params.set('active-state', selected?.statename);
            params.delete('active-tract');
            params.delete('active-county');
            params.set('active-panel', 'visualization');
          } else if (selected.type === 'county') {
            params.set(
              'active-county',
              generateCountyId(selected?.countyname, selected?.statename)
            );
            params.delete('active-tract');
            params.delete('active-state');
            params.set('active-panel', 'visualization');
          } else if (selected.type === 'tract') {
            params.delete('active-county');
            params.delete('active-state');
            params.set('active-tract', selected?.tractcode);
            params.set('active-panel', 'visualization');
          }

          params.delete('active-compare-tract');
          params.delete('active-compare-county');
          params.delete('active-compare-state');
          params.delete('active-compare-similar-tract');

          return params;
        });
      }

      setSelected(selected);
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (
      searchParams.get('active-state') === null &&
      searchParams.get('active-county') === null &&
      searchParams.get('active-tract') === null
    ) {
      setSelected(null);
    }

    // Init search params for broadband filters if unset.
    if (searchParams.get('broadband-min') === null) {
      setSearchParams((params) => {
        params.set('broadband-min', '0');

        return params;
      });
    }

    if (searchParams.get('broadband-max') === null) {
      setSearchParams((params) => {
        params.set('broadband-max', '5');

        return params;
      });
    }

    if (searchParams.get('active-state') !== null) {
      const currenctStatesByStatenameSource = statesByStatenamesSource(
        [searchParams.get('active-state')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currenctStatesByStatenameSource.data,
        queryParameters: currenctStatesByStatenameSource.queryParameters,
        connection: currenctStatesByStatenameSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            zoomIntoView({
              lng: result[0].lng,
              lat: result[0].lat,
              zoom: 5,
            });

            setSearchQuery(result[0]?.statename);
            setSelected(result[0]);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve state ${searchParams.get('active-state')}`
          );
        });
    } else if (searchParams.get('active-county') !== null) {
      const currenctCountiesByCountyIdsSource = countiesByCountyIdsSource(
        [searchParams.get('active-county')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currenctCountiesByCountyIdsSource.data,
        queryParameters: currenctCountiesByCountyIdsSource.queryParameters,
        connection: currenctCountiesByCountyIdsSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            zoomIntoView({
              lng: result[0].lng,
              lat: result[0].lat,
              zoom: 6,
            });

            setSearchQuery(result[0]?.countyname);
            setSelected(result[0]);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve county ${searchParams.get('active-county')}`
          );
        });
    } else if (searchParams.get('active-tract') !== null) {
      const currenctTractsByTractcodesSource = tractsByTractcodesSource(
        [searchParams.get('active-tract')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currenctTractsByTractcodesSource.data,
        queryParameters: currenctTractsByTractcodesSource.queryParameters,
        connection: currenctTractsByTractcodesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            zoomIntoView({
              lng: result[0].lng,
              lat: result[0].lat,
              zoom: 12,
            });

            setSearchQuery(result[0]?.tractcode);
            setSelected(result[0]);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-tract')}`
          );
        });
    }
  }, [
    credentials,
    searchParams,
    setSearchParams,
    setSelected,
    updateSelected,
    year,
    zoomIntoView,
  ]);

  useEffect(() => {
    const items = [
      {
        id: 'year',
        title: 'Year',
        content: (
          <InputRadioLabelGroup value={year} setValue={setYear} items={YEARS_OPTIONS} />
        ),
      },
      {
        id: 'broadband',
        title: 'Broadband Score',
        content: (
          <div className='pt-7'>
            <Slider.Root
              defaultValue={[
                searchParams.get('broadband-min') || '0',
                searchParams.get('broadband-max') || '5',
              ]}
              className='relative flex items-center select-none touch-none h-5 w-[calc(100%_-_20px)]'
              max={5}
              value={[
                searchParams.get('broadband-min') || '0',
                searchParams.get('broadband-max') || '5',
              ]}
              onValueChange={(values) => {
                setSearchParams((params) => {
                  params.set('broadband-min', values[0]);
                  params.set('broadband-max', values[1]);

                  return params;
                });
              }}
            >
              <Slider.Track className='relative grow rounded-full h-[2px] bg-[#f3f5f7]'>
                <Slider.Range className='absolute h-full opacity-100 bg-button-gradient' />
                <div className='absolute flex items-center justify-between w-full'>
                  <div className='opacity-0 w-1 h-0.5'></div>
                  <div className='bg-white-400 w-1 h-0.5'></div>
                  <div className='bg-white-400 w-1 h-0.5'></div>
                  <div className='bg-white-400 w-1 h-0.5'></div>
                  <div className='bg-white-400 w-1 h-0.5'></div>
                  <div className='opacity-0 w-1 h-0.5'></div>
                </div>
              </Slider.Track>
              <Slider.Thumb className='relative block w-3.5 h-3.5 bg-button-gradient border-white-400 border-2 shadow-sm rounded-full'>
                <div className='absolute bottom-3.5 w-[30px] h-[30px] -translate-x-[10px] flex items-center justify-center'>
                  <SliderLabel />
                  <span className='text-white-400 text-base font-bold absolute -translate-y-0.5'>
                    {searchParams.get('broadband-min') || '0'}
                  </span>
                </div>
              </Slider.Thumb>
              <Slider.Thumb className='relative block w-3.5 h-3.5 bg-button-gradient border-white-400 border-2 shadow-sm rounded-full'>
                <div className='absolute bottom-3.5 w-[30px] h-[30px] -translate-x-[10px] flex items-center justify-center'>
                  <SliderLabel />
                  <span className='text-white-400 text-base font-bold absolute -translate-y-0.5'>
                    {searchParams.get('broadband-max') || '5'}
                  </span>
                </div>
              </Slider.Thumb>
            </Slider.Root>
          </div>
        ),
      },
      {
        id: 'socioeconomic',
        title: 'Socioeconomic Factors',
        content: (
          <div className='peer-focus-within:hidden'>
            <NestedRadioInputs
              value={socioeconomicFactor}
              setValue={setSocioeconomicFactor}
              items={features}
            />
          </div>
        ),
      },
    ];

    if (selected) {
      setFilteredItems(items.filter((item) => item.id !== 'broadband'));
    } else {
      setFilteredItems(items);
    }
  }, [
    features,
    searchParams,
    selected,
    setSearchParams,
    setSocioeconomicFactor,
    setYear,
    socioeconomicFactor,
    year,
  ]);

  let handleCompareTractsClick = function () {
    setSearchParams((params) => {
      params.set('active-panel', TRACT_PANEL_TYPES.COMPARE_TRACTS);

      return params;
    });
  };

  let handleCompareCountiesClick = function () {
    setSearchParams((params) => {
      params.set('active-panel', COUNTY_PANEL_TYPES.COMPARE_COUNTIES);

      return params;
    });
  };

  let handleCompareStatesClick = function () {
    setSearchParams((params) => {
      params.set('active-panel', STATE_PANEL_TYPES.COMPARE_STATES);

      return params;
    });
  };

  let decideLayerToggleActionsVisibility = function () {
    return !(
      (searchResults || isSearchResultsLoading) &&
      !selected &&
      searchQuery?.length > 0
    );
  };

  const handleFiltersClear = function () {
    setSearchParams((params) => {
      params.set('broadband-min', '0');
      params.set('broadband-max', '5');
      params.set('socioeconomic-factor', 'overall_socioeconomic');
      params.set('year', YEARS_OPTIONS[YEARS_OPTIONS.length - 1]);

      return params;
    });
  };

  const _compareButtonItems = [
    {
      label: 'Tracts',
      action: handleCompareTractsClick,
    },
    {
      label: 'Counties',
      action: handleCompareCountiesClick,
    },
    {
      label: 'States',
      action: handleCompareStatesClick,
    },
  ];

  let isClearFiltersButtonvisible =
    searchParams.get('broadband-min') !== '0' ||
    searchParams.get('broadband-max') !== '5' ||
    searchParams.get('socioeconomic-factor') !== 'overall_socioeconomic' ||
    searchParams.get('year') !== YEARS_OPTIONS[YEARS_OPTIONS.length - 1];

  return (
    <div
      className='h-full max-h-full overflow-hidden shadow-md bg-white-400 rounded-xl'
      data-component-name='FilterBox'
    >
      <div className='peer'>
        <SearchBox
          searchQuery={searchQuery}
          searchResults={searchResults}
          setSearchQuery={setSearchQuery}
          isSearchResultsLoading={isSearchResultsLoading}
          updateSelected={updateSelected}
          selected={selected}
        />
      </div>

      <div
        className={`px-5 py-6 overflow-y-auto ${
          isLayerToggleActionsOpen === true
            ? 'max-h-[calc(100vh_-_404px)]'
            : 'max-h-[calc(100vh_-_335px)]'
        }  h-full text-base -translate-y-px border-t border-blue-100 border-solid`}
      >
        {selected?.type === 'state' && (
          <div className='grid grid-cols-2 gap-2.5'>
            <Button action={() => handleCompareStatesClick()}>
              <span className='text-base'>Compare states</span>
            </Button>
          </div>
        )}

        {selected?.type === 'county' && (
          <div className='grid grid-cols-2 gap-2.5'>
            <Button action={() => handleCompareCountiesClick()}>
              <span className='text-base'>Compare counties</span>
            </Button>
          </div>
        )}

        {selected?.type === 'tract' && (
          <div className='grid grid-cols-2 gap-2.5'>
            {/* <ButtonSelect items={compareButtonItems} label='Compare' /> */}
            <Button action={() => handleCompareTractsClick()}>
              <span className='text-base'>Compare tracts</span>
            </Button>
            <Button action={() => handleShowSimilarTractsClick()}>
              <span className='text-base'>Show similar tracts</span>
            </Button>
          </div>
        )}

        <div className={`flex justify-between items-center ${selected && 'mt-5'}`}>
          <h2 className={`text-base font-bold text-blue-600`}>Filter</h2>
          <button
            type='button'
            className={`flex gap-[5px] items-center text-orange-500 text-base transition-opacity duration-100 ${
              isClearFiltersButtonvisible === false ? 'opacity-0 pointer-events-none' : ''
            }`}
            onClick={() => handleFiltersClear()}
          >
            <span>Clear all filters</span>
            <CloseLarge className='text-orange-500 translate-y-px' />
          </button>
        </div>

        <div className='mt-1'>
          <Accordion items={filteredItems} />
        </div>
      </div>

      {decideLayerToggleActionsVisibility() === true && (
        <LayerToggleActions
          onOpenChange={setIsLayerToggleActionsOpen}
          open={isLayerToggleActionsOpen}
        >
          <div className='flex flex-col gap-4'>
            <InputToggle
              isActive={isSocioeconomicFactorsLayerActive}
              setIsActive={setIsSocioeconomicFactorsLayerActive}
              label='Socioeconomic factors'
            />
            {/* <div className='pt-5 border-t border-blue-100'>
              <InputRadioGroup
                items={broadbandLayers}
                value={activeBroadbandLayer}
                setValue={setActiveBroadbandLayer}
              />
            </div> */}
          </div>
        </LayerToggleActions>
      )}
    </div>
  );
}
